import { useEffect, useState, useMemo, useRef } from 'react';
import { motion, useScroll, useTransform, useSpring, useMotionValue } from 'framer-motion';

// Generate random stars for the night sky
const generateStars = (count) => {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 50,
    size: Math.random() * 2.5 + 0.5,
    delay: Math.random() * 3,
    duration: Math.random() * 2 + 1.5,
  }));
};

const Star = ({ x, y, size, delay, duration, opacity }) => (
  <motion.div
    className="absolute rounded-full bg-white"
    style={{
      left: `${x}%`,
      top: `${y}%`,
      width: size,
      height: size,
      willChange: 'opacity, transform',
    }}
    animate={{
      opacity: [0.3 * opacity, 1 * opacity, 0.3 * opacity],
      scale: [1, 1.3, 1],
    }}
    transition={{
      duration: duration,
      delay: delay,
      repeat: Infinity,
      ease: 'easeInOut',
    }}
  />
);

// Shooting Star component (GPU optimized)
const ShootingStar = ({ delay }) => {
  const startX = 20 + Math.random() * 40;
  const startY = 5 + Math.random() * 20;
  
  return (
    <motion.div
      className="absolute"
      style={{
        left: `${startX}%`,
        top: `${startY}%`,
        willChange: 'transform, opacity',
      }}
      initial={{ opacity: 0, x: 0, y: 0 }}
      animate={{
        opacity: [0, 1, 1, 0],
        x: [0, 150, 200],
        y: [0, 100, 140],
      }}
      transition={{
        duration: 1.2,
        delay: delay,
        repeat: Infinity,
        repeatDelay: 8 + Math.random() * 4,
        ease: 'easeOut',
      }}
    >
      {/* Shooting star trail */}
      <div
        style={{
          width: '80px',
          height: '2px',
          background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.8) 50%, white 100%)',
          transform: 'rotate(35deg)',
          transformOrigin: 'right center',
          borderRadius: '2px',
          boxShadow: '0 0 8px rgba(255,255,255,0.4)',
        }}
      />
    </motion.div>
  );
};

// Child-friendly cartoon cloud - fluffy puffy style (GPU optimized)
const CartoonCloud = ({ index, opacity }) => {
  const positions = [
    { top: 8, size: 180, duration: 85 },
    { top: 25, size: 140, duration: 95 },
    { top: 40, size: 200, duration: 75 },
    { top: 18, size: 160, duration: 110 },
    { top: 48, size: 130, duration: 90 },
  ];
  
  const pos = positions[index % positions.length];
  const startDelay = index * 18;

  return (
    <motion.div
      className="absolute pointer-events-none"
      style={{
        top: `${pos.top}%`,
        width: pos.size,
        height: pos.size * 0.5,
        opacity: opacity,
        willChange: 'transform',
        transform: 'translateZ(0)',
      }}
      initial={{ x: '110vw' }}
      animate={{ x: '-30vw' }}
      transition={{
        duration: pos.duration,
        repeat: Infinity,
        ease: 'linear',
        delay: startDelay,
      }}
    >
      <svg viewBox="0 0 200 80" className="w-full h-full">
        {/* Cartoon puffy cloud - overlapping circles */}
        <ellipse cx="50" cy="50" rx="35" ry="25" fill="rgba(255,255,255,0.95)" />
        <ellipse cx="85" cy="40" rx="40" ry="30" fill="rgba(255,255,255,0.98)" />
        <ellipse cx="130" cy="45" rx="45" ry="28" fill="rgba(255,255,255,0.95)" />
        <ellipse cx="160" cy="52" rx="30" ry="22" fill="rgba(255,255,255,0.9)" />
        {/* Top bumps */}
        <ellipse cx="70" cy="30" rx="25" ry="20" fill="rgba(255,255,255,0.97)" />
        <ellipse cx="110" cy="25" rx="30" ry="22" fill="rgba(255,255,255,0.98)" />
        <ellipse cx="145" cy="32" rx="22" ry="18" fill="rgba(255,255,255,0.95)" />
      </svg>
    </motion.div>
  );
};

// Smaller cartoon cloud for variety (GPU optimized)
const SmallCartoonCloud = ({ index, opacity }) => {
  const positions = [
    { top: 15, size: 100, duration: 100 },
    { top: 35, size: 120, duration: 115 },
    { top: 52, size: 90, duration: 95 },
    { top: 22, size: 110, duration: 125 },
  ];
  
  const pos = positions[index % positions.length];
  const startDelay = index * 22 + 10;

  return (
    <motion.div
      className="absolute pointer-events-none"
      style={{
        top: `${pos.top}%`,
        width: pos.size,
        height: pos.size * 0.5,
        opacity: opacity * 0.8,
        willChange: 'transform',
        transform: 'translateZ(0)',
      }}
      initial={{ x: '115vw' }}
      animate={{ x: '-25vw' }}
      transition={{
        duration: pos.duration,
        repeat: Infinity,
        ease: 'linear',
        delay: startDelay,
      }}
    >
      <svg viewBox="0 0 150 60" className="w-full h-full">
        {/* Smaller cartoon cloud */}
        <ellipse cx="35" cy="38" rx="25" ry="18" fill="rgba(255,255,255,0.9)" />
        <ellipse cx="65" cy="32" rx="30" ry="22" fill="rgba(255,255,255,0.95)" />
        <ellipse cx="100" cy="36" rx="32" ry="20" fill="rgba(255,255,255,0.92)" />
        <ellipse cx="125" cy="40" rx="20" ry="15" fill="rgba(255,255,255,0.88)" />
        {/* Top bumps */}
        <ellipse cx="55" cy="22" rx="18" ry="14" fill="rgba(255,255,255,0.93)" />
        <ellipse cx="85" cy="18" rx="22" ry="16" fill="rgba(255,255,255,0.95)" />
      </svg>
    </motion.div>
  );
};

// Linear interpolation helper
const lerp = (a, b, t) => a + (b - a) * t;

// Smooth color interpolation between two RGB values
const lerpColor = (color1, color2, t) => {
  return {
    r: Math.round(lerp(color1.r, color2.r, t)),
    g: Math.round(lerp(color1.g, color2.g, t)),
    b: Math.round(lerp(color1.b, color2.b, t)),
  };
};

// Define key color stops for smooth gradient transition
const SKY_COLORS = {
  // Night (0-20%)
  nightTop: { r: 5, g: 10, b: 31 },
  nightMid: { r: 10, g: 26, b: 63 },
  nightBottom: { r: 14, g: 46, b: 110 },
  
  // Dawn (20-40%)
  dawnTop: { r: 10, g: 26, b: 63 },
  dawnMid: { r: 60, g: 80, b: 130 },
  dawnBottom: { r: 180, g: 120, b: 100 },
  
  // Sunrise (40-60%)
  sunriseTop: { r: 60, g: 100, b: 150 },
  sunriseMid: { r: 200, g: 160, b: 140 },
  sunriseBottom: { r: 255, g: 190, b: 130 },
  
  // Morning (60-80%)
  morningTop: { r: 25, g: 140, b: 220 },
  morningMid: { r: 25, g: 164, b: 251 },
  morningBottom: { r: 100, g: 200, b: 255 },
  
  // Day (80-100%)
  dayTop: { r: 25, g: 164, b: 251 },
  dayMid: { r: 25, g: 164, b: 251 },
  dayBottom: { r: 135, g: 206, b: 250 },
};

// Get smooth sky gradient based on progress (0-1)
const getSmoothSkyGradient = (progress) => {
  let topColor, midColor, bottomColor;
  
  if (progress < 0.2) {
    // Night phase
    const t = progress / 0.2;
    topColor = SKY_COLORS.nightTop;
    midColor = lerpColor(SKY_COLORS.nightTop, SKY_COLORS.nightMid, t);
    bottomColor = lerpColor(SKY_COLORS.nightMid, SKY_COLORS.nightBottom, t);
  } else if (progress < 0.4) {
    // Night to Dawn transition
    const t = (progress - 0.2) / 0.2;
    topColor = lerpColor(SKY_COLORS.nightTop, SKY_COLORS.dawnTop, t);
    midColor = lerpColor(SKY_COLORS.nightMid, SKY_COLORS.dawnMid, t);
    bottomColor = lerpColor(SKY_COLORS.nightBottom, SKY_COLORS.dawnBottom, t);
  } else if (progress < 0.6) {
    // Dawn to Sunrise transition
    const t = (progress - 0.4) / 0.2;
    topColor = lerpColor(SKY_COLORS.dawnTop, SKY_COLORS.sunriseTop, t);
    midColor = lerpColor(SKY_COLORS.dawnMid, SKY_COLORS.sunriseMid, t);
    bottomColor = lerpColor(SKY_COLORS.dawnBottom, SKY_COLORS.sunriseBottom, t);
  } else if (progress < 0.8) {
    // Sunrise to Morning transition
    const t = (progress - 0.6) / 0.2;
    topColor = lerpColor(SKY_COLORS.sunriseTop, SKY_COLORS.morningTop, t);
    midColor = lerpColor(SKY_COLORS.sunriseMid, SKY_COLORS.morningMid, t);
    bottomColor = lerpColor(SKY_COLORS.sunriseBottom, SKY_COLORS.morningBottom, t);
  } else {
    // Morning to Day transition
    const t = (progress - 0.8) / 0.2;
    topColor = lerpColor(SKY_COLORS.morningTop, SKY_COLORS.dayTop, t);
    midColor = lerpColor(SKY_COLORS.morningMid, SKY_COLORS.dayMid, t);
    bottomColor = lerpColor(SKY_COLORS.morningBottom, SKY_COLORS.dayBottom, t);
  }
  
  return `linear-gradient(180deg, 
    rgb(${topColor.r}, ${topColor.g}, ${topColor.b}) 0%, 
    rgb(${midColor.r}, ${midColor.g}, ${midColor.b}) 50%, 
    rgb(${bottomColor.r}, ${bottomColor.g}, ${bottomColor.b}) 100%)`;
};

export const DreamBackground = () => {
  const { scrollYProgress } = useScroll();
  const [mounted, setMounted] = useState(false);
  const [skyGradient, setSkyGradient] = useState(getSmoothSkyGradient(0));
  const [showShootingStars, setShowShootingStars] = useState(true);
  
  // Reduced star count for better performance
  const stars = useMemo(() => generateStars(40), []);
  const shootingStars = useMemo(() => [0, 1], []); // 2 shooting stars

  // Very smooth spring for gradient transitions
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 30,
    damping: 30,
    restDelta: 0.001
  });

  // Transform values for elements
  const starOpacity = useTransform(smoothProgress, [0, 0.25, 0.45], [1, 0.5, 0]);
  const cloudOpacity = useTransform(smoothProgress, [0, 0.3, 0.7, 1], [0.15, 0.25, 0.4, 0.5]);
  const shootingStarOpacity = useTransform(smoothProgress, [0, 0.15, 0.3], [1, 0.5, 0]);

  // Update sky gradient on scroll progress change
  useEffect(() => {
    const unsubscribe = smoothProgress.on("change", (v) => {
      setSkyGradient(getSmoothSkyGradient(v));
      // Hide shooting stars when scrolled down
      setShowShootingStars(v < 0.25);
    });
    return unsubscribe;
  }, [smoothProgress]);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  return (
    <div
      className="fixed inset-0 z-0 pointer-events-none overflow-hidden"
      style={{ 
        background: skyGradient,
        willChange: 'background',
        transform: 'translateZ(0)',
      }}
    >
      {/* Stars Layer */}
      <motion.div 
        className="absolute inset-0"
        style={{ opacity: starOpacity }}
      >
        {stars.map((star) => (
          <Star
            key={star.id}
            x={star.x}
            y={star.y}
            size={star.size}
            delay={star.delay}
            duration={star.duration}
            opacity={1}
          />
        ))}
      </motion.div>

      {/* Shooting Stars Layer - only visible at top of page */}
      {showShootingStars && (
        <motion.div 
          className="absolute inset-0"
          style={{ opacity: shootingStarOpacity }}
        >
          {shootingStars.map((index) => (
            <ShootingStar key={`shooting-${index}`} delay={index * 5 + 2} />
          ))}
        </motion.div>
      )}

      {/* Cartoon Clouds Layer 1 - Reduced count for performance */}
      <motion.div className="absolute inset-0 overflow-hidden" style={{ opacity: cloudOpacity }}>
        {[0, 1, 2, 3].map((index) => (
          <CartoonCloud key={`cartoon-${index}`} index={index} opacity={1} />
        ))}
      </motion.div>

      {/* Smaller Cartoon Clouds Layer 2 - Reduced count for performance */}
      <motion.div className="absolute inset-0 overflow-hidden" style={{ opacity: cloudOpacity }}>
        {[0, 1, 2].map((index) => (
          <SmallCartoonCloud key={`small-${index}`} index={index} opacity={0.85} />
        ))}
      </motion.div>
    </div>
  );
};

// Sun layer only - grass is handled separately in App.js
export const SkyForeground = () => {
  const { scrollYProgress } = useScroll();
  const [mounted, setMounted] = useState(false);

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 30,
    damping: 30,
    restDelta: 0.0001
  });

  // Sun transforms
  const sunY = useTransform(smoothProgress, [0.4, 1], [300, 60]);
  const sunOpacity = useTransform(smoothProgress, [0.4, 0.6, 1], [0, 0.9, 1]);
  const sunScale = useTransform(smoothProgress, [0.4, 1], [0.5, 1.3]);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  return (
    <div className="fixed inset-0 z-[5] pointer-events-none overflow-hidden">
      {/* Sun - behind content but above background */}
      <motion.div
        className="absolute right-[15%] pointer-events-none"
        style={{ 
          bottom: 100,
          y: sunY,
          opacity: sunOpacity,
          scale: sunScale,
        }}
      >
        {/* Outer warm glow */}
        <div
          className="absolute rounded-full"
          style={{
            width: 320,
            height: 320,
            background: 'radial-gradient(circle, rgba(255, 200, 100, 0.4) 0%, rgba(255, 160, 80, 0.15) 40%, transparent 70%)',
            transform: 'translate(-50%, 50%)',
            left: '50%',
            bottom: '50%',
          }}
        />
        {/* Middle glow */}
        <div
          className="absolute rounded-full"
          style={{
            width: 180,
            height: 180,
            background: 'radial-gradient(circle, rgba(255, 230, 150, 0.6) 0%, rgba(255, 200, 100, 0.25) 50%, transparent 80%)',
            transform: 'translate(-50%, 50%)',
            left: '50%',
            bottom: '50%',
          }}
        />
        {/* Sun core */}
        <div
          className="w-24 h-24 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(255, 255, 240, 1) 0%, rgba(255, 235, 150, 0.95) 40%, rgba(255, 200, 100, 0.8) 80%)',
            boxShadow: '0 0 80px rgba(255, 200, 100, 0.6), 0 0 120px rgba(255, 180, 80, 0.3)',
          }}
        />
      </motion.div>
    </div>
  );
};
